import { useState, useEffect, useRef } from "react"
import "../Css/FileViewer.css"
import { getAuth } from "firebase/auth"
import { useFileContent } from "../hooks/use-file-content"
import {cleanFileName} from "../util/clear-fileName"

export default function FileViewer({ file, isOpen, onClose }) {
  const baseURL = process.env.REACT_APP_URLBASE
  const [content, setContent] = useState(null)
  const [viewMode, setViewMode] = useState("microsoft")
  const [localError, setLocalError] = useState(null)
  const [scriptPreloaded, setScriptPreloaded] = useState(false)

  const auth = getAuth()
  const user = auth.currentUser

  const { loadFileContent, invalidateCache, loading, error, cacheStats } = useFileContent(baseURL)

  // 🚀 OPTIMIZACIÓN 1: Pre-carga del script de OnlyOffice
  useEffect(() => {
    if (isOpen && !window.DocsAPI && !scriptPreloaded) {
      const onlyOfficeUrl = process.env.REACT_APP_DOCKER_OFFICE
      if (onlyOfficeUrl) {
        console.log("🔧 Pre-cargando script de OnlyOffice...")
        const script = document.createElement("script")
        script.src = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`
        script.async = true
        script.defer = true

        script.onload = () => {
          console.log("✅ Script de OnlyOffice pre-cargado exitosamente")
          setScriptPreloaded(true)
        }

        script.onerror = () => {
          console.log("❌ Error al pre-cargar script de OnlyOffice")
        }

        // Verificar si el script ya existe
        const existingScript = document.querySelector(`script[src*="api/documents/api.js"]`)
        if (!existingScript) {
          document.head.appendChild(script)
        } else {
          setScriptPreloaded(true)
        }
      }
    }
  }, [isOpen, scriptPreloaded])

  const getFileType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension)) return "image"
    if (["mp4", "mov", "avi", "webm"].includes(extension)) return "video"
    if (["mp3", "wav", "ogg"].includes(extension)) return "audio"
    if (extension === "pdf") return "pdf"
    if (["txt", "md", "json", "xml", "csv"].includes(extension)) return "text"
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(extension)) return "office"
    return "unknown"
  }

  // Cargar contenido cuando se abre el modal
  useEffect(() => {
    if (isOpen && file && user) {
      setLocalError(null)
      loadFileContent(file, user).then((url) => {
        if (url) setContent(url)
      })
    }
  }, [isOpen, file, user, loadFileContent])

  // Limpiar estado cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setContent(null)
      setViewMode("microsoft")
      setLocalError(null)
    }
  }, [isOpen])

  // Función para forzar refresh (invalidar cache)
  const handleRefresh = async () => {
    if (file && user) {
      setLocalError(null)
      invalidateCache(file, user)
      const url = await loadFileContent(file, user, true)
      if (url) setContent(url)
    }
  }

  const renderOfficeDocument = () => {
    if (!content) return null

    if (viewMode === "microsoft") {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(content)}`

      return (
        <div className="office-viewer">
          <div className="office-toolbar">
            <span className="file-type-indicator">📄 {file.fileName}</span>
            <div className="viewer-controls">
              <button
                onClick={() => setViewMode("onlyoffice")}
                className="switch-btn"
                title="Cambiar a OnlyOffice para edición"
              >
                🔧 Modo Edición
                {scriptPreloaded && <span className="preload-indicator">⚡</span>}
              </button>
            </div>
          </div>
          <iframe
            src={officeViewerUrl}
            width="100%"
            height="calc(100% - 40px)"
            frameBorder="0"
            title={file.fileName}
            style={{ border: "none" }}
          />
        </div>
      )
    } else {
      return (
        <OnlyOfficePortal
          file={file}
          content={content}
          onBack={() => setViewMode("microsoft")}
          baseURL={baseURL}
          scriptPreloaded={scriptPreloaded}
        />
      )
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="viewer-loading">
          <div className="loading-spinner"></div>
          <p>Cargando archivo...</p>
          <div className="loading-details">
            <small>
              Cache: {cacheStats.size}/{cacheStats.maxSize} archivos
            </small>
            {scriptPreloaded && <small className="script-status">⚡ OnlyOffice listo</small>}
          </div>
        </div>
      )
    }

    const displayError = error || localError
    if (displayError) {
      return (
        <div className="viewer-error">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar el archivo</h3>
          <p>{displayError}</p>
          <div className="error-actions">
            <button onClick={handleRefresh} className="retry-btn">
              🔄 Reintentar
            </button>
            {content && (
              <a
                href={content}
                download={file?.fileName}
                className="download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                📥 Descargar
              </a>
            )}
          </div>
        </div>
      )
    }

    if (!content) return null

    const fileType = getFileType(file.fileName)

    switch (fileType) {
      case "image":
        return (
          <div className="image-viewer">
            <img
              src={content || "/placeholder.svg"}
              alt={file.fileName}
              onError={() => setLocalError("Error al cargar la imagen")}
            />
          </div>
        )

      case "video":
        return (
          <div className="video-viewer">
            <video controls>
              <source src={content} />
              Tu navegador no soporta la reproducción de video.
            </video>
          </div>
        )

      case "audio":
        return (
          <div className="audio-viewer">
            <div className="audio-info">
              <div className="audio-icon">🎵</div>
              <h3>{file.fileName}</h3>
            </div>
            <audio controls>
              <source src={content} />
              Tu navegador no soporta la reproducción de audio.
            </audio>
          </div>
        )

      case "pdf":
        return (
          <div className="pdf-viewer">
            <iframe src={`${content}#toolbar=1&navpanes=1&scrollbar=1`} title={file.fileName} />
          </div>
        )

      case "text":
        return (
          <div className="text-viewer">
            <iframe src={content} title={file.fileName} />
          </div>
        )

      case "office":
        return renderOfficeDocument()

      default:
        return (
          <div className="unsupported-viewer">
            <div className="unsupported-icon">📁</div>
            <h3>Vista previa no disponible</h3>
            <p>Este tipo de archivo no se puede previsualizar.</p>
            <a
              href={content}
              download={file.fileName}
              className="download-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              📥 Descargar archivo
            </a>
          </div>
        )
    }
  }

  if (!isOpen) return null

  return (
    <div className="file-viewer-overlay" onClick={onClose}>
      <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="viewer-header">
          <div className="file-info">
            <h2>{file?.fileName}</h2>
            <span className="file-type">{getFileType(file?.fileName || "")}</span>
            <span className="cache-indicator" title={`Cache: ${cacheStats.size} archivos`}>
              💾
            </span>
            {scriptPreloaded && (
              <span className="script-indicator" title="OnlyOffice pre-cargado">
                ⚡
              </span>
            )}
          </div>
          <div className="viewer-actions">
            <button onClick={handleRefresh} className="refresh-action" title="Actualizar archivo">
              🔄
            </button>
            {content && (
              <a href={content} download={file?.fileName} className="download-action" title="Descargar">
                📥
              </a>
            )}
            <button onClick={onClose} className="close-btn" title="Cerrar">
              ✕
            </button>
          </div>
        </div>

        <div className="viewer-content">{renderContent()}</div>
      </div>
    </div>
  )
}

// 🚀 OPTIMIZACIÓN 2: Portal OnlyOffice con indicadores de progreso detallados
function OnlyOfficePortal({ file, content, onBack, baseURL, scriptPreloaded }) {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState(null)
  const [loadingStage, setLoadingStage] = useState("initializing") // initializing, script, document, app, ready
  const [loadingProgress, setLoadingProgress] = useState(0)
  const portalRef = useRef(null)
  const editorRef = useRef(null)
  const onlyOfficeUrl = process.env.REACT_APP_DOCKER_OFFICE

  const getOnlyOfficeTypes = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase()
    if (["doc", "docx"].includes(ext)) return { fileType: "docx", documentType: "word" }
    if (["xls", "xlsx"].includes(ext)) return { fileType: "xlsx", documentType: "cell" }
    if (["ppt", "pptx"].includes(ext)) return { fileType: "pptx", documentType: "slide" }
    return null
  }

  const getStageMessage = (stage) => {
    switch (stage) {
      case "initializing":
        return "🔧 Inicializando..."
      case "script":
        return "📥 Cargando OnlyOffice..."
      case "document":
        return "📄 Preparando documento..."
      case "app":
        return "🔄 Cargando aplicación..."
      case "ready":
        return "✅ Listo para editar"
      default:
        return "⏳ Cargando..."
    }
  }

  useEffect(() => {
    // Crear contenedor portal fuera del árbol de React
    const portalContainer = document.createElement("div")
    portalContainer.id = `onlyoffice-portal-${Date.now()}`
    portalContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: white;
      z-index: 1001;
      display: flex;
      flex-direction: column;
    `

    document.body.appendChild(portalContainer)
    portalRef.current = portalContainer

    // Crear toolbar con indicadores de progreso mejorados
    const toolbar = document.createElement("div")
    toolbar.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 1rem;
      background: #f8f9fa;
      border-bottom: 1px solid #e5e7eb;
      font-size: 0.875rem;
    `

    const backBtn = document.createElement("button")
    backBtn.textContent = "← Vista rápida"
    backBtn.style.cssText = `
      background: #3b82f6;
      color: white;
      border: none;
      padding: 0.25rem 0.75rem;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.875rem;
    `
    backBtn.onclick = () => {
      cleanup()
      onBack()
    }

    const title = document.createElement("span")
    title.textContent = `🔧 Modo Edición - ${file.fileName}`
    title.style.fontWeight = "500"

    const statusContainer = document.createElement("div")
    statusContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.25rem;
    `

    const status = document.createElement("span")
    status.style.color = "#6b7280"
    status.style.fontSize = "0.875rem"

    const progressBar = document.createElement("div")
    progressBar.style.cssText = `
      width: 120px;
      height: 4px;
      background: #e5e7eb;
      border-radius: 2px;
      overflow: hidden;
    `

    const progressFill = document.createElement("div")
    progressFill.style.cssText = `
      height: 100%;
      background: #3b82f6;
      width: 0%;
      transition: width 0.3s ease;
    `
    progressBar.appendChild(progressFill)

    statusContainer.appendChild(status)
    statusContainer.appendChild(progressBar)

    toolbar.appendChild(backBtn)
    toolbar.appendChild(title)
    toolbar.appendChild(statusContainer)
    portalContainer.appendChild(toolbar)

    // Crear contenedor para OnlyOffice
    const editorContainer = document.createElement("div")
    const editorId = `editor-${Date.now()}`
    editorContainer.id = editorId
    editorContainer.style.cssText = `
      flex: 1;
      width: 100%;
      background: #f5f5f5;
      position: relative;
    `
    portalContainer.appendChild(editorContainer)

    // Función para actualizar progreso
    const updateProgress = (stage, progress) => {
      setLoadingStage(stage)
      setLoadingProgress(progress)
      status.textContent = getStageMessage(stage)
      progressFill.style.width = `${progress}%`
    }

    const cleanup = () => {
      if (editorRef.current) {
        try {
          editorRef.current.destroyEditor?.()
        } catch (err) {
          console.log("Error al limpiar editor:", err)
        }
        editorRef.current = null
      }

      if (portalRef.current && document.body.contains(portalRef.current)) {
        document.body.removeChild(portalRef.current)
      }
    }

    const initOnlyOffice = async () => {
      try {
        updateProgress("initializing", 10)

        // Si el script ya está pre-cargado, saltar esta etapa
        if (!window.DocsAPI) {
          updateProgress("script", 20)

          const script = document.createElement("script")
          script.src = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`
          script.async = true

          await new Promise((resolve, reject) => {
            script.onload = () => {
              updateProgress("script", 40)
              resolve()
            }
            script.onerror = reject
            document.head.appendChild(script)
          })
        } else {
          console.log("✅ Usando script pre-cargado de OnlyOffice")
          updateProgress("script", 40)
        }

        updateProgress("document", 50)

        const officeConfig = getOnlyOfficeTypes(file.fileName)
        if (!officeConfig) {
          updateProgress("error", 0)
          setError("Tipo de archivo no soportado")
          return
        }

        updateProgress("document", 60)
        
        // ✅ CAMBIO AQUÍ: Limpiar también el r2Key para el documentKey
        const cleanedFileName = cleanFileName(file.fileName)
        const cleanedR2Key = cleanFileName(file.r2Key || file.fileName)
        const cleanDocumentKey = `${cleanedR2Key}_${Date.now()}`
        
        console.log("🔍 DEBUGGING COMPLETO:")
        console.log("fileName original:", file.fileName)
        console.log("fileName limpio:", cleanedFileName)
        console.log("r2Key original:", file.r2Key)
        console.log("r2Key limpio:", cleanedR2Key)
        console.log("documentKey final:", cleanDocumentKey)

        console.log("fileName:",  cleanFileName(file.fileName))
        const config = {
          document: {
            fileType: officeConfig.fileType,
            title: cleanFileName(file.fileName),
            url: `${baseURL}/onlyoffice-proxy/${file.r2Key}`,
            key: cleanDocumentKey,
          },
          documentType: officeConfig.documentType,
          editorConfig: {
            mode: "edit",
            user: {
              id: "user123",
              name: "Usuario",
            },
          },
          width: "100%",
          height: "100%",
          documentServerUrl: onlyOfficeUrl,
          events: {
            onAppReady: () => {
              console.log("📱 OnlyOffice App Ready")
              updateProgress("app", 80)
            },
            onDocumentReady: () => {
              console.log("📄 Documento OnlyOffice listo")
              updateProgress("ready", 100)
              setIsReady(true)

              // Ocultar barra de progreso después de 2 segundos
              setTimeout(() => {
                progressBar.style.display = "none"
              }, 2000)
            },
            onError: (event) => {
              console.error("❌ Error en OnlyOffice:", event)
              updateProgress("error", 0)
              setError("Error al cargar el documento: " + JSON.stringify(event))
            },
            onInfo: (event) => {
              console.log("ℹ️ Info OnlyOffice:", event)
            },
          },
        }
        

        updateProgress("app", 70)
        editorRef.current = new window.DocsAPI.DocEditor(editorId, config)
      } catch (err) {
        console.error("❌ Error inicializando OnlyOffice:", err)
        updateProgress("error", 0)
        setError("Error al inicializar OnlyOffice: " + err.message)
      }
    }

    initOnlyOffice()

    // Cleanup al desmontar
    return cleanup
  }, [file, content, baseURL, onBack, onlyOfficeUrl])

  // Manejar tecla ESC para cerrar
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onBack()
      }
    }

    document.addEventListener("keydown", handleEsc)
    return () => document.removeEventListener("keydown", handleEsc)
  }, [onBack])

  if (error) {
    return (
      <div className="onlyoffice-error">
        <div className="error-icon">⚠️</div>
        <h3>Error en OnlyOffice</h3>
        <p>{error}</p>
        <div className="error-details">
          <small>Etapa: {loadingStage}</small>
          <small>Progreso: {loadingProgress}%</small>
        </div>
        <button onClick={onBack} className="back-btn">
          ← Volver a vista rápida
        </button>
      </div>
    )
  }

  // El portal se renderiza directamente en el DOM
  return null
}
  
